import React from 'react'
import {
	IonButton,
    IonIcon,
} from '@ionic/react'
import { PoseGroup } from 'react-pose'
import { Haptics, ImpactStyle } from '@capacitor/haptics'
import GridCell from './GridCell'
import PlayerName from './PlayerName'
import {
    useReady,
	useSetAmDragging,
} from './data'
import { FixedItem, DraggableItemPlayer } from './definitionsPoses'
import { RxDragHandleDots2 } from 'react-icons/rx'
import { TiTimes } from 'react-icons/ti'
import { ellipse } from 'ionicons/icons'

type GridPlayersProps = {
	players: Array<PlayerId>
	data: AllRosterStats | undefined | null
	editMode?: boolean
	onDrop?: (e: React.DragEvent, payload: OnDropObject) => void
	available: { [playerId: PlayerId]: boolean }
	toggleAvailable: (playerId: PlayerId) => void
	numPlayersShown: number
	showAllPlayers: boolean
	setShowAllPlayers: (showAll: boolean) => void
	showPositionPreferences: boolean
	onDragStart: () => void
}

const GridPlayers = (props: GridPlayersProps) => {
	const { players, data, editMode, onDrop, available } = props

    const ready = useReady()

	const setAmDragging = useSetAmDragging()

    const allowDrag = ready && editMode

	const onDragStart = () => {
		props.onDragStart()
		setAmDragging(true)
		Haptics.impact({ style: ImpactStyle.Light })
	}

	const pulsePlayers: Array<PlayerId> = [ ]

	//
	// Warn the user when a player is set to "available" but has no rostered positions,
	// or if they're unavailable but have rostered positions.
	//
	if (ready && editMode && data) {
		const playersWithNoPositions = Object.entries(data).filter(([ _, playerRosterStats ]) =>
																   playerRosterStats.filter(obj => !obj || !obj.playing || obj.position !== '-').length === 0
																  ).map(([ playerId, _ ]) => playerId)

		const unavailablePlayersWithPositions = Object.entries(available).filter(([ playerId, isAvailable ]) => {
			if (!isAvailable) {
				const playerRosterStats = data[playerId]
				if (playerRosterStats?.some(obj => obj && obj.position !== '-')) {
					return true
				}
			}
			return false
		}).map(([ playerId, _ ]) => playerId)

		pulsePlayers.push(...playersWithNoPositions, ...unavailablePlayersWithPositions)

		// console.log('GridPlayers', { data, playersWithNoPositions, unavailablePlayersWithPositions, pulsePlayers, available })
	}

    return (
        <div className="column column-players">
			<FixedItem className="fixed-element" key="top-left-void">
				<GridCell type="void" content="" />
			</FixedItem>
            <PoseGroup>
                {
					players.map((player, index) => {
						const isAvailable = available[player]
						const isDraggable = allowDrag && isAvailable && !props.showPositionPreferences
						const ItemPlayer = isDraggable ? DraggableItemPlayer : FixedItem

						const myClasses = [ ]
						if (allowDrag && !props.showPositionPreferences) {
							myClasses.push('posed-element')
							if (!isDraggable) {
								myClasses.push('not-draggable')
							}
						} else {
							myClasses.push('fixed-element')
						}
						if (index >= props.numPlayersShown) {
							myClasses.push('hide')
						}
						if (pulsePlayers.includes(player)) {
							myClasses.push('animated-pulse')
						}
						return (
							<ItemPlayer
								key={allowDrag ? player : index}
								className={myClasses.join(' ')}
								onDragStart={onDragStart}
								onDragEnd={e => onDrop && onDrop(e, { player: player }) }
								isAvailable={isAvailable}
							>
								<CellPlayer
									isDraggable={isDraggable}
									playerId={player}
									isAvailable={isAvailable}
									toggleAvailable={props.toggleAvailable}
								/>
							</ItemPlayer>
						)
					})
				}
			</PoseGroup>
			{
				(editMode && players.length > props.numPlayersShown) ? (
					<FixedItem className="fixed-element" key="bottom-left-void">
						<IonButton
							className="num-players-shown-button"
							disabled={!ready}
							expand="block"
							fill="clear"
							size="small"
							onClick={() => props.setShowAllPlayers(true)}
						>
							{`+ ${players.length - props.numPlayersShown} More Player${players.length - props.numPlayersShown > 1 ? 's' : ''}`}
						</IonButton>
					</FixedItem>
				) : (editMode && props.showAllPlayers) ? (
					<FixedItem className="fixed-element" key="bottom-left-void">
						<IonButton
							className="num-players-shown-button"
							disabled={!ready}
							expand="block"
							fill="clear"
							size="small"
							onClick={() => props.setShowAllPlayers(false)}
						>
							Hide extra players
						</IonButton>
					</FixedItem>
				) : null
			}
		</div>
	)
}

type CellPlayerProps = {
	playerId: PlayerId
	isAvailable?: boolean
	isDraggable?: boolean
	toggleAvailable: (playerId: PlayerId) => void
}

//
// Don't re-render cells unless the contents change
//
const CellPlayer = React.memo((props: CellPlayerProps) => {

	const { playerId, isAvailable } = props

	const playerAvailable = isAvailable ? (
        <IonIcon icon={ellipse} className="tick" />
	) : (
		<TiTimes className="cross" />
	)

	const content = (
		<>
			{props.isDraggable && <RxDragHandleDots2 className="drag-handle" />}
			<PlayerName playerId={playerId} />
		</>
	)

	const onClickIcon = (_i: I, e: React.MouseEvent) => {
		e.preventDefault()
		props.toggleAvailable(playerId)
	}

	return (
		<GridCell
			type="player"
            content={content}
			icons={[ playerAvailable ]}
			onClickIcon={onClickIcon}
			isAvailable={isAvailable}
			i={playerId}
		/>
	)
})

CellPlayer.displayName = 'CellPlayer'

export default GridPlayers
